import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../lib/request';
import { normalizePreferenceUsername, readPreferredGuideStepAiModel, savePreferredGuideStepAiModel } from '../lib/guideStepAiPreferences';

const initialState = {
  stepId: null, title: '', promptHint: '', model: '',
  previousStepsContextCount: 2, followingStepsContextCount: 0,
  generatedText: '', loading: false, error: '',
  connectionLoading: false, connectionError: '', connectionNotice: '',
  connection: null, revision: null, models: [], available: false,
  settingsUnavailable: false, showRemoteUploader: false
};

function readSelection(response) {
  const connections = response?.settings?.connections;
  const catalog = response?.ai?.guideStepGeneration;
  if (!connections || !Array.isArray(connections.items)
    || !Number.isSafeInteger(connections.revision) || !catalog) {
    throw new Error('Unable to read your AI connections. Refresh and try again.');
  }
  const connection = connections.items.find((item) => item.id === connections.activeConnectionId) || null;
  const models = Array.isArray(catalog.models) ? catalog.models.filter((model) => model.supportsImages === true) : [];
  const modelConfigurationInvalid = catalog.unavailableReason === 'ai_model_config_invalid';
  return {
    connection, revision: connections.revision, models,
    model: models.some((model) => model.id === catalog.defaultModel) ? catalog.defaultModel : '',
    connectionError: modelConfigurationInvalid
      ? 'The AI model configuration is invalid. Ask an administrator to review it.' : '',
    available: !modelConfigurationInvalid && response.settingsUnavailable !== true && catalog.available === true
      && !!connection && catalog.provider === connection.provider && models.length > 0,
    settingsUnavailable: response.settingsUnavailable === true
  };
}

function selectionKey(state) {
  return JSON.stringify([state.connection?.id, state.connection?.provider, state.revision,
    state.available, state.models.map((model) => model.id)]);
}

// Modal-local state: refreshing settings never replaces the guide or its unsaved draft.
export default function useGuideStepAi(contextKey, username) {
  const preferenceUser = normalizePreferenceUsername(username);
  const [state, setState] = useState(initialState);
  const current = useRef(initialState);
  const epoch = useRef(0);
  const reads = useRef(0);
  const generation = useRef(0);
  const mounted = useRef(true);
  const update = useCallback((change) => {
    const next = typeof change === 'function' ? change(current.current) : change;
    current.current = next;
    if (mounted.current) setState(next);
  }, []);

  const close = useCallback(() => {
    epoch.current += 1;
    reads.current += 1;
    generation.current += 1;
    update(initialState);
  }, [update]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; epoch.current += 1; };
  }, []);
  useEffect(() => { close(); }, [contextKey, preferenceUser, close]);

  const refresh = useCallback(async () => {
    if (!current.current.stepId) return;
    const startedEpoch = epoch.current;
    const readId = ++reads.current;
    update((prev) => ({ ...prev, connectionLoading: true, connectionError: '' }));
    const isCurrent = () => mounted.current && epoch.current === startedEpoch && reads.current === readId;
    try {
      const response = await api.get('/api/v1/settings', { timeout: 20000 });
      if (!isCurrent()) return;
      if (preferenceUser && normalizePreferenceUsername(response?.profile?.username) !== preferenceUser) {
        throw new Error('Your account changed. Reload the page before using AI connections.');
      }
      const selection = readSelection(response);
      const previous = current.current;
      const rememberedModel = selection.available
        ? readPreferredGuideStepAiModel(preferenceUser, selection.connection.provider, selection.models) : '';
      const changed = previous.revision !== null && selectionKey(previous) !== selectionKey(selection);
      if (changed) generation.current += 1;
      update({
        ...previous, ...selection, connectionLoading: false,
        model: selection.connection?.provider === previous.connection?.provider
          && selection.models.some((model) => model.id === previous.model)
          ? previous.model : rememberedModel || selection.model,
        ...(changed ? { generatedText: '', loading: false,
          connectionNotice: selection.connectionError ? ''
            : 'Your AI connections changed. Review the destination and click Generate again.' } : {})
      });
    } catch (error) {
      if (!isCurrent()) return;
      generation.current += 1;
      update((prev) => ({ ...prev, connectionLoading: false, available: false, loading: false,
        generatedText: '', connectionError: error?.message || 'Unable to load your AI connections. Refresh and try again.' }));
    }
  }, [preferenceUser, update]);

  const open = useCallback((draft) => {
    close();
    update({ ...initialState, ...draft });
    refresh();
  }, [close, update, refresh]);

  useEffect(() => {
    if (!state.stepId) return undefined;
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [state.stepId, refresh]);

  const change = useCallback((updater) => {
    generation.current += 1;
    update((prev) => ({ ...updater(prev), generatedText: '', loading: false }));
  }, [update]);

  const selectModel = useCallback((model) => {
    const snapshot = current.current;
    if (!snapshot.stepId || !snapshot.available || snapshot.connectionLoading || snapshot.loading
      || !snapshot.connection || !snapshot.models.some((entry) => entry.id === model)) return;
    change((prev) => ({ ...prev, model }));
    savePreferredGuideStepAiModel(preferenceUser, snapshot.connection.provider, model);
  }, [change, preferenceUser]);

  const generate = useCallback(async (url, guide) => {
    const snapshot = current.current;
    if (snapshot.loading || snapshot.connectionLoading || !snapshot.available
      || !snapshot.connection || !snapshot.models.some((model) => model.id === snapshot.model)) return;
    const startedEpoch = epoch.current;
    const requestId = ++generation.current;
    const isCurrent = () => mounted.current && epoch.current === startedEpoch && generation.current === requestId;
    update((prev) => ({ ...prev, loading: true, generatedText: '', error: '', connectionNotice: '' }));
    try {
      const response = await api.post(url, {
        guide, stepId: snapshot.stepId, promptHint: snapshot.promptHint, model: snapshot.model,
        previousStepsContextCount: snapshot.previousStepsContextCount,
        followingStepsContextCount: snapshot.followingStepsContextCount,
        expectedConnectionId: snapshot.connection.id, expectedConnectionsRevision: snapshot.revision
      }, { timeout: 240000 });
      if (!isCurrent()) return;
      if (!response?.ok || typeof response.generatedText !== 'string' || !response.generatedText.trim()
        || response.provider !== snapshot.connection.provider || response.model !== snapshot.model) {
        throw new Error('AI step generation returned no usable result. Try again.');
      }
      update((prev) => ({ ...prev, loading: false, generatedText: response.generatedText }));
    } catch (error) {
      if (!isCurrent()) return;
      const conflict = ['connections_revision_conflict', 'connections_selection_required', 'connections_migration_required'].includes(error?.code || error?.errorCode);
      update((prev) => ({ ...prev, loading: false, generatedText: '',
        error: conflict ? 'Your AI connections changed. Review the updated destination and click Generate again.'
          : error?.message || 'AI step generation failed. Try again.' }));
      if (conflict) await refresh();
    }
  }, [refresh, update]);

  return { state, change, selectModel, open, close, refresh, generate };
}
